/**
 * CustomIntegrationForm
 * Form for creating or editing custom OAuth/API integrations with MCP tool definitions
 */

import React, { useState, useEffect } from 'react';
import { X, Loader2, CheckCircle2, AlertCircle, Link2, Plus, Trash2 } from 'lucide-react';
import { Input, Dropdown, Button, FormSection } from '../../ui';
import api from '../../../services/api';
import { AuthType, CustomIntegration, HttpMethod, CustomToolDefinition, OAuthCredentials, ApiKeyCredentials, BasicAuthCredentials } from '../../../types';
import styles from './CustomIntegrationForm.module.css';

export interface CustomIntegrationFormProps {
  /** Existing integration to edit (if any) */
  integration?: CustomIntegration;
  /** Callback when integration is saved */
  onSave: (integration: CustomIntegration) => void;
  /** Callback when form is closed */
  onClose: () => void;
}

export const CustomIntegrationForm: React.FC<CustomIntegrationFormProps> = ({
  integration,
  onSave,
  onClose,
}) => {
  const [name, setName] = useState(integration?.name || '');
  const [description, setDescription] = useState(integration?.description || '');
  const [authType, setAuthType] = useState<AuthType>(integration?.authType || 'oauth2');
  const [baseUrl, setBaseUrl] = useState(integration?.baseUrl || '');
  const [iconUrl, setIconUrl] = useState(integration?.iconUrl || '');
  
  // Tools state
  const [tools, setTools] = useState<CustomToolDefinition[]>(integration?.tools || []);
  
  // OAuth credentials
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [authorizationUrl, setAuthorizationUrl] = useState('');
  const [tokenUrl, setTokenUrl] = useState('');
  const [scopes, setScopes] = useState('');
  
  // API Key credentials
  const [apiKey, setApiKey] = useState('');
  
  // Basic Auth credentials
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Populate form when editing
  useEffect(() => {
    if (integration) {
      setDescription(integration.description || '');
      setTools(integration.tools || []);
    }
  }, [integration]);

  const getCredentials = (): OAuthCredentials | ApiKeyCredentials | BasicAuthCredentials | null => {
    switch (authType) {
      case 'oauth2':
        if (!clientId || !clientSecret || !authorizationUrl || !tokenUrl) {
          return null;
        }
        return {
          clientId,
          clientSecret,
          authorizationUrl,
          tokenUrl,
          scopes: scopes || '',
        };
      case 'api-key':
        if (!apiKey) {
          return null;
        }
        return { apiKey };
      case 'basic-auth':
        if (!username || !password) {
          return null;
        }
        return { username, password };
      default:
        return null;
    }
  };

  const validateForm = (): boolean => {
    if (!name.trim()) {
      setError('Integration name is required');
      return false;
    }
    if (!baseUrl.trim()) {
      setError('Base URL is required');
      return false;
    }
    if (!getCredentials()) {
      setError('Please fill in all required credential fields');
      return false;
    }
    setError(null);
    return true;
  };

  const handleTestConnection = async () => {
    if (!validateForm()) {
      return;
    }

    const credentials = getCredentials();
    if (!credentials) {
      setError('Invalid credentials');
      return;
    }

    setTesting(true);
    setTestResult(null);
    setError(null);

    try {
      const response = await api.testCustomIntegration({
        authType,
        baseUrl,
        credentials: credentials as unknown as Record<string, unknown>,
      });

      if (response.success && response.data) {
        setTestResult({
          success: response.data.success,
          message: response.data.message,
        });
      } else {
        setTestResult({
          success: false,
          message: response.error || 'Connection test failed',
        });
      }
    } catch (err: any) {
      setTestResult({
        success: false,
        message: err.message || 'Connection test failed',
      });
    } finally {
      setTesting(false);
    }
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    const credentials = getCredentials();
    if (!credentials) {
      setError('Invalid credentials');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      let response;
      if (integration) {
        // Update existing integration
        response = await api.updateCustomIntegration(integration.id, {
          name,
          description,
          authType,
          baseUrl,
          iconUrl: iconUrl || undefined,
          credentials: credentials as unknown as Record<string, unknown>,
          tools,
        });
      } else {
        // Create new integration
        response = await api.createCustomIntegration({
          name,
          description,
          authType,
          baseUrl,
          iconUrl: iconUrl || undefined,
          credentials: credentials as unknown as Record<string, unknown>,
          tools,
        });
      }

      if (response.success && response.data) {
        onSave(response.data);
      } else {
        setError(response.error || 'Failed to save integration');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to save integration');
    } finally {
      setSaving(false);
    }
  };

  const authTypeOptions = [
    { value: 'oauth2', label: 'OAuth 2.0' },
    { value: 'api-key', label: 'API Key / PAT' },
    { value: 'basic-auth', label: 'Basic Auth' },
  ];

  const methodOptions = [
    { value: 'GET', label: 'GET' },
    { value: 'POST', label: 'POST' },
    { value: 'PUT', label: 'PUT' },
    { value: 'PATCH', label: 'PATCH' },
    { value: 'DELETE', label: 'DELETE' },
  ];

  const paramTypeOptions = [
    { value: 'string', label: 'string' },
    { value: 'number', label: 'number' },
    { value: 'boolean', label: 'boolean' },
    { value: 'integer', label: 'integer' },
  ];

  /** Add a new empty tool */
  const addTool = () => {
    const newTool: CustomToolDefinition = {
      name: '',
      description: '',
      method: 'GET',
      path: '/',
      inputSchema: {
        type: 'object',
        properties: {},
        required: [],
      },
    };
    setTools([...tools, newTool]);
  };

  /** Remove a tool by index */
  const removeTool = (index: number) => {
    setTools(tools.filter((_, i) => i !== index));
  };

  /** Update a specific tool field */
  const updateTool = (index: number, field: keyof CustomToolDefinition, value: unknown) => {
    const updated = [...tools];
    updated[index] = { ...updated[index], [field]: value };
    setTools(updated);
  };

  /** Add a parameter to a tool's inputSchema */
  const addToolParam = (toolIndex: number) => {
    const updated = [...tools];
    const tool = { ...updated[toolIndex] };
    const paramName = `param${Object.keys(tool.inputSchema.properties).length + 1}`;
    tool.inputSchema = {
      ...tool.inputSchema,
      properties: {
        ...tool.inputSchema.properties,
        [paramName]: { type: 'string', description: '' },
      },
    };
    updated[toolIndex] = tool;
    setTools(updated);
  };

  /** Remove a parameter from a tool's inputSchema */
  const removeToolParam = (toolIndex: number, paramName: string) => {
    const updated = [...tools];
    const tool = { ...updated[toolIndex] };
    const { [paramName]: _, ...rest } = tool.inputSchema.properties;
    tool.inputSchema = {
      ...tool.inputSchema,
      properties: rest,
      required: (tool.inputSchema.required || []).filter(r => r !== paramName),
    };
    updated[toolIndex] = tool;
    setTools(updated);
  };

  /** Update a parameter name (rename the key) */
  const updateParamName = (toolIndex: number, oldName: string, newName: string) => {
    if (!newName || newName === oldName) return;
    const updated = [...tools];
    const tool = { ...updated[toolIndex] };
    const entries = Object.entries(tool.inputSchema.properties);
    const newProperties: Record<string, { type: string; description?: string }> = {};
    for (const [key, val] of entries) {
      newProperties[key === oldName ? newName : key] = val;
    }
    tool.inputSchema = {
      ...tool.inputSchema,
      properties: newProperties,
      required: (tool.inputSchema.required || []).map(r => r === oldName ? newName : r),
    };
    updated[toolIndex] = tool;
    setTools(updated);
  };

  /** Update a parameter's type or description */
  const updateParamField = (
    toolIndex: number,
    paramName: string,
    field: 'type' | 'description',
    value: string
  ) => {
    const updated = [...tools];
    const tool = { ...updated[toolIndex] };
    tool.inputSchema = {
      ...tool.inputSchema,
      properties: {
        ...tool.inputSchema.properties,
        [paramName]: {
          ...tool.inputSchema.properties[paramName],
          [field]: value,
        },
      },
    };
    updated[toolIndex] = tool;
    setTools(updated);
  };

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <div className={styles.headerIcon}>
            <Link2 size={24} />
          </div>
          <div className={styles.headerText}>
            <h2>{integration ? 'Edit Custom Integration' : 'Add Custom Integration'}</h2>
            <p>Connect any service with your own credentials</p>
          </div>
          <button className={styles.closeButton} onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className={styles.modalBody}>
          <FormSection title="Basic Information">
            <div className={styles.formGroup}>
              <label>Integration Name *</label>
              <Input
                type="text"
                placeholder="e.g., My Notion API"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>

            <div className={styles.formGroup}>
              <label>Description (Optional)</label>
              <Input
                type="text"
                placeholder="Describe what this integration connects to"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>

            <div className={styles.formGroup}>
              <label>Base URL *</label>
              <Input
                type="url"
                placeholder="https://api.example.com"
                value={baseUrl}
                onChange={(e) => setBaseUrl(e.target.value)}
              />
              <span className={styles.hint}>The service endpoint URL</span>
            </div>

            <div className={styles.formGroup}>
              <label>Icon URL (Optional)</label>
              <Input
                type="url"
                placeholder="https://example.com/logo.png"
                value={iconUrl}
                onChange={(e) => setIconUrl(e.target.value)}
              />
            </div>

            <div className={styles.formGroup}>
              <label>Authentication Type *</label>
              <Dropdown
                value={authType}
                onChange={(value) => setAuthType(value as AuthType)}
                options={authTypeOptions}
              />
            </div>
          </FormSection>

          <FormSection title="Credentials">
            {authType === 'oauth2' && (
              <>
                <div className={styles.formGroup}>
                  <label>Client ID *</label>
                  <Input
                    type="text"
                    placeholder="Enter client ID"
                    value={clientId}
                    onChange={(e) => setClientId(e.target.value)}
                  />
                </div>

                <div className={styles.formGroup}>
                  <label>Client Secret *</label>
                  <Input
                    type="password"
                    placeholder="Enter client secret"
                    value={clientSecret}
                    onChange={(e) => setClientSecret(e.target.value)}
                  />
                </div>

                <div className={styles.formGroup}>
                  <label>Authorization URL *</label>
                  <Input
                    type="url"
                    placeholder="https://example.com/oauth/authorize"
                    value={authorizationUrl}
                    onChange={(e) => setAuthorizationUrl(e.target.value)}
                  />
                </div>

                <div className={styles.formGroup}>
                  <label>Token URL *</label>
                  <Input
                    type="url"
                    placeholder="https://example.com/oauth/token"
                    value={tokenUrl}
                    onChange={(e) => setTokenUrl(e.target.value)}
                  />
                </div>

                <div className={styles.formGroup}>
                  <label>Scopes (Optional)</label>
                  <Input
                    type="text"
                    placeholder="read write"
                    value={scopes}
                    onChange={(e) => setScopes(e.target.value)}
                  />
                  <span className={styles.hint}>Space-separated list of OAuth scopes</span>
                </div>
              </>
            )}

            {authType === 'api-key' && (
              <div className={styles.formGroup}>
                <label>API Key / Personal Access Token *</label>
                <Input
                  type="password"
                  placeholder="Enter your API key or PAT"
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                />
              </div>
            )}

            {authType === 'basic-auth' && (
              <>
                <div className={styles.formGroup}>
                  <label>Username *</label>
                  <Input
                    type="text"
                    placeholder="Enter username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                  />
                </div>

                <div className={styles.formGroup}>
                  <label>Password *</label>
                  <Input
                    type="password"
                    placeholder="Enter password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </div>
              </>
            )}
          </FormSection>

          <FormSection title="MCP Tools (Optional)">
            <p className={styles.toolsDescription}>
              Define API endpoints as MCP tools. These tools become available to AI assistants and workflows.
            </p>
            <div className={styles.toolsList}>
              {tools.map((tool, toolIndex) => (
                <div key={toolIndex} className={styles.toolCard}>
                  <div className={styles.toolCardHeader}>
                    <span className={styles.toolCardTitle}>Tool {toolIndex + 1}</span>
                    <button
                      type="button"
                      className={styles.removeToolBtn}
                      onClick={() => removeTool(toolIndex)}
                      title="Remove tool"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>

                  <div className={styles.toolRow}>
                    <div>
                      <span className={styles.toolFieldLabel}>Tool Name *</span>
                      <Input
                        type="text"
                        placeholder="search_items"
                        value={tool.name}
                        onChange={(e) => updateTool(toolIndex, 'name', e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_'))}
                      />
                    </div>
                    <div>
                      <span className={styles.toolFieldLabel}>Description *</span>
                      <Input
                        type="text"
                        placeholder="Search for items"
                        value={tool.description}
                        onChange={(e) => updateTool(toolIndex, 'description', e.target.value)}
                      />
                    </div>
                  </div>

                  <div className={styles.toolRowMethodPath}>
                    <div>
                      <span className={styles.toolFieldLabel}>Method</span>
                      <Dropdown
                        value={tool.method}
                        onChange={(value) => updateTool(toolIndex, 'method', value)}
                        options={methodOptions}
                      />
                    </div>
                    <div>
                      <span className={styles.toolFieldLabel}>Path *</span>
                      <Input
                        type="text"
                        placeholder="/v1/search"
                        value={tool.path}
                        onChange={(e) => updateTool(toolIndex, 'path', e.target.value)}
                      />
                    </div>
                  </div>

                  {/* Parameters */}
                  <div className={styles.toolRowFull}>
                    <span className={styles.toolFieldLabel}>Parameters</span>
                    <div className={styles.paramList}>
                      {Object.entries(tool.inputSchema.properties).map(([paramName, paramDef]) => (
                        <div key={paramName} className={styles.paramRow}>
                          <Input
                            type="text"
                            placeholder="param name"
                            value={paramName}
                            onBlur={(e) => updateParamName(toolIndex, paramName, e.target.value)}
                          />
                          <Dropdown
                            value={paramDef.type}
                            onChange={(value) => updateParamField(toolIndex, paramName, 'type', value)}
                            options={paramTypeOptions}
                          />
                          <Input
                            type="text"
                            placeholder="description"
                            value={paramDef.description || ''}
                            onChange={(e) => updateParamField(toolIndex, paramName, 'description', e.target.value)}
                          />
                          <button
                            type="button"
                            className={styles.removeParamBtn}
                            onClick={() => removeToolParam(toolIndex, paramName)}
                            title="Remove parameter"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                    <button
                      type="button"
                      className={styles.addButton}
                      onClick={() => addToolParam(toolIndex)}
                      style={{ marginTop: 'var(--space-2)' }}
                    >
                      <Plus size={12} /> Add Parameter
                    </button>
                  </div>
                </div>
              ))}

              {tools.length === 0 && (
                <div className={styles.emptyTools}>
                  No tools defined yet. Add tools to expose API endpoints as MCP tools.
                </div>
              )}
            </div>

            <div style={{ marginTop: 'var(--space-3)' }}>
              <button
                type="button"
                className={styles.addButton}
                onClick={addTool}
              >
                <Plus size={14} /> Add Tool
              </button>
            </div>
          </FormSection>

          {testResult && (
            <div className={`${styles.testResult} ${testResult.success ? styles.success : styles.error}`}>
              {testResult.success ? (
                <>
                  <CheckCircle2 size={16} />
                  <span>{testResult.message}</span>
                </>
              ) : (
                <>
                  <AlertCircle size={16} />
                  <span>{testResult.message}</span>
                </>
              )}
            </div>
          )}

          {error && (
            <div className={styles.errorMessage}>
              <AlertCircle size={16} />
              <span>{error}</span>
            </div>
          )}
        </div>

        <div className={styles.modalFooter}>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="secondary"
            onClick={handleTestConnection}
            disabled={testing || !name || !baseUrl}
          >
            {testing ? (
              <>
                <Loader2 size={16} className={styles.spinning} />
                Testing...
              </>
            ) : (
              'Test Connection'
            )}
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={saving || !name || !baseUrl}
          >
            {saving ? (
              <>
                <Loader2 size={16} className={styles.spinning} />
                Saving...
              </>
            ) : (
              integration ? 'Update Integration' : 'Save Integration'
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CustomIntegrationForm;
